'use client';

import { useState, useEffect } from 'react';
import { Save, Link2 } from 'lucide-react';
import { useContent } from '@/contexts/ContentContext';

const input = 'w-full bg-white/[0.05] border border-white/[0.08] rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500/50 resize-none';

export default function SettingsPage() {
  const { selectedProjectId, projects } = useContent();
  const [brandVoice, setBrandVoice] = useState('');
  const [pillars, setPillars] = useState('');
  const [mediaFolder, setMediaFolder] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!selectedProjectId) return;
    try {
      const data = JSON.parse(localStorage.getItem(`beast:content-settings:${selectedProjectId}`) || '{}');
      setBrandVoice(data.brandVoice || '');
      setPillars(data.pillars || '');
      setMediaFolder(data.mediaFolder || '');
    } catch {}
  }, [selectedProjectId]);

  const handleSave = () => {
    if (!selectedProjectId) return;
    localStorage.setItem(`beast:content-settings:${selectedProjectId}`, JSON.stringify({ brandVoice, pillars, mediaFolder }));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const proj = projects.find(p => p.id === selectedProjectId);

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-xl font-semibold text-white">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">{proj ? `Configure ${proj.name}` : 'Select a project first'}</p>
      </div>

      {!selectedProjectId ? (
        <div className="text-center py-12 glass-card">
          <p className="text-gray-500">Select a project from the sidebar</p>
        </div>
      ) : (
        <>
          <div className="glass-card p-6 space-y-5">
            <h3 className="text-sm font-semibold text-cyan-400 uppercase tracking-wider">Brand & Voice</h3>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Brand Voice</label>
              <textarea value={brandVoice} onChange={e => setBrandVoice(e.target.value)} rows={3} placeholder="Professional, bold, educational..." className={input} />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Content Pillars</label>
              <textarea value={pillars} onChange={e => setPillars(e.target.value)} rows={3} placeholder="Key topics, one per line..." className={input} />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Media Folder</label>
              <input value={mediaFolder} onChange={e => setMediaFolder(e.target.value)} placeholder="~/projects/media" className={input} />
            </div>
            <button onClick={handleSave} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-cyan-500 text-black hover:bg-cyan-400">
              <Save className="w-4 h-4" /> {saved ? 'Saved!' : 'Save Settings'}
            </button>
          </div>

          <div className="glass-card p-6 space-y-4">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Platform Connections</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 bg-[rgb(var(--background))]/50 rounded-lg">
                <div className="flex items-center gap-2"><span>💼</span><span className="text-sm text-white">LinkedIn</span></div>
                <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-gray-400 border border-white/[0.08] hover:border-gray-700 hover:text-white">
                  <Link2 className="w-3.5 h-3.5" /> Connect
                </button>
              </div>
              {['Instagram', 'YouTube', 'TikTok', 'X'].map(p => (
                <div key={p} className="flex items-center justify-between p-3 bg-[rgb(var(--background))]/50 rounded-lg opacity-50">
                  <span className="text-sm text-gray-400">{p}</span>
                  <span className="text-xs text-gray-600">Coming soon</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
